import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import appConfig from '../../config/app.config';

/**
 * What to read after this.
 *
 * Every card states why it is here — "More from this series", "Same topic",
 * "Also about Angular". A recommendation a reader cannot explain to themselves
 * is just a list, and the reason is what makes it worth following.
 *
 * Renders nothing at all when there is nothing to suggest. An empty
 * "Recommended" heading is worse than no heading.
 */

interface Suggestion {
  title: string;
  slug: string;
  blogSlug: string;
  blogTitle: string;
  courseTitle?: string;
  readTime?: number;
  reason: string;
  readers: number;
}

export function ReadNext({
  blogSlug,
  postSlug,
  heading = 'Read next',
}: {
  blogSlug?: string;
  postSlug?: string;
  /** Omit the slugs to show what is popular across the site instead. */
  heading?: string;
}) {
  const [items, setItems] = useState<Suggestion[] | null>(null);

  useEffect(() => {
    const path =
      blogSlug && postSlug
        ? `/discover/related/${encodeURIComponent(blogSlug)}/${encodeURIComponent(postSlug)}`
        : '/discover/popular?limit=4';

    let cancelled = false;

    fetch(`${appConfig.apiBaseUrl}${path}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => !cancelled && setItems(data?.items ?? []))
      .catch(() => !cancelled && setItems([]));

    return () => {
      cancelled = true;
    };
  }, [blogSlug, postSlug]);

  if (!items || items.length === 0) return null;

  return (
    <section aria-labelledby="read-next" className="mt-14 sm:mt-16">
      <h2
        id="read-next"
        className="font-headline text-xl sm:text-2xl font-bold text-foreground mb-5"
      >
        {heading}
      </h2>

      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {items.map((item) => (
          <li key={`${item.blogSlug}/${item.slug}`}>
            <Link
              to={`/blog/${item.blogSlug}/${item.slug}`}
              className="group flex h-full flex-col rounded-2xl border border-border bg-surface-container-lowest p-5 transition-colors hover:border-primary"
            >
              <span className="text-xs font-bold uppercase tracking-widest text-secondary mb-2.5">
                {item.reason}
              </span>

              <span className="font-headline text-lg font-bold leading-snug text-foreground group-hover:text-primary transition-colors">
                {item.title}
              </span>

              <span className="mt-auto pt-3 text-sm text-on-surface-variant">
                {[
                  item.courseTitle ?? item.blogTitle,
                  item.readTime ? `${item.readTime} min` : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

export default ReadNext;
